import {formatPhone} from 'app/utilities/StringHelpers/StringHelpers';

const CELLULAR_SUFFIX_PATTERN = /,?\s*(\d+\s+)?מנוי(?:י)?\s*סלולר\s*$/;
const CELLULAR_SEGMENT_PATTERN = /^(\d+\s+)?מנוי(?:י)?\s*סלולר$/;
const CELLULAR_LABEL = 'מנוי סלולר';
const CELLULAR_LABEL_PLURAL = 'מנויי סלולר';

export const getSubscriberBanNumber = (subscriber) => subscriber?.banNumber ?? subscriber?.accountNumber ?? subscriber?.ban ?? '';

export const getSubscriberPhoneNumber = (subscriber) =>
  subscriber?.productSubscriberNo ?? subscriber?.subscriberNumber ?? subscriber?.cli ?? '';

/**
 * Removes backend cellular suffix from productDescriptions (e.g. ", 3 מנוי סלולר").
 */
export const stripCellularSuffix = (description) => {
  if (!description || typeof description !== 'string') return '';
  return description.replace(CELLULAR_SUFFIX_PATTERN, '').trim();
};

/**
 * Removes cellular segments from productDescriptions wherever they appear
 */
export const stripCellularSegments = (description) => {
  if (!description || typeof description !== 'string') return '';
  return description
    .split(/[,|]/)
    .map((part) => part.trim())
    .filter((part) => part && !CELLULAR_SEGMENT_PATTERN.test(part))
    .join(', ');
};

/** Replaces comma-separated product names with pipe separators (Figma). */
export const normalizeBaseText = (description) => {
  if (!description) return '';
  return description
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' | ');
};

/**
 * Returns cellular subscribers that belong to the given BAN.
 */
export const getCellularSubscribersForBan = (banNumber, subscribersByProduct) => {
  if (banNumber == null || banNumber === '') return [];
  const cellularList = subscribersByProduct?.Cellular;
  if (!Array.isArray(cellularList) || cellularList.length === 0) return [];

  const banKey = String(banNumber);
  return cellularList.filter((subscriber) => String(getSubscriberBanNumber(subscriber)) === banKey);
};

export const formatCellularPhoneNumbers = (cellularSubscribers) =>
  cellularSubscribers.map(getSubscriberPhoneNumber).filter(Boolean).map(formatPhone);

/**
 * @returns {{
 *   baseText: string,
 *   cellularText: string|null,
 *   phoneNumbers: string[],
 *   showTooltip: boolean,
 *   layout: 'fallback'|'only-cellular'|'with-base'
 * }}
 */
export const buildProductDescriptionParts = (productDescriptions, banNumber, subscribersByProduct) => {
  const cellularSubscribers = getCellularSubscribersForBan(banNumber, subscribersByProduct);
  const rawPhones = cellularSubscribers.map(getSubscriberPhoneNumber).filter(Boolean);
  const phoneNumbers = formatCellularPhoneNumbers(cellularSubscribers);

  if (rawPhones.length === 0) {
    return {
      baseText: normalizeBaseText(productDescriptions?.trim() || ''),
      cellularText: null,
      phoneNumbers: [],
      showTooltip: false,
      layout: 'fallback',
    };
  }

  const baseText = normalizeBaseText(stripCellularSegments(productDescriptions));

  if (rawPhones.length === 1) {
    const phone = rawPhones[0];
    if (!baseText) {
      return {
        baseText: '',
        cellularText: `${CELLULAR_LABEL} ${formatPhone(phone)} `,
        phoneNumbers,
        showTooltip: false,
        layout: 'only-cellular',
      };
    }
    return {
      baseText,
      cellularText: `${CELLULAR_LABEL} ${formatPhone(phone)}`,
      phoneNumbers,
      showTooltip: false,
      layout: 'with-base',
    };
  }

  return {
    baseText,
    cellularText: `${rawPhones.length} ${CELLULAR_LABEL_PLURAL}`,
    phoneNumbers,
    showTooltip: true,
    layout: baseText ? 'with-base' : 'only-cellular',
  };
};
